/**
 * ONE navigation editor, two authorities (#511).
 *
 * Christopher: *"I need the same object so I can set up the navbar from an
 * admin perspective — the default anyone would see."* So this component is the
 * same object in both places. What differs is not the editor, it is which
 * function the host writes through:
 *
 *   a user editing their own bar   → `core.pin_navigation(document | null)`
 *   an admin editing a persona's   → `core.set_navigation(persona, document, host)`
 *                                    / `core.clear_navigation(persona, host)`
 *
 * ── the CASCADE is drawn, not asserted (#1466 B3, owner rulings M2/M3/O3) ──
 * A bar is `platform recipe → deployment default → your role's default → your
 * own pin`, and until this slice no screen said so: `core.navigation()`
 * "deliberately answers what the bar IS, not which layer it came from"
 * (`2026081012:240`), so the tree carried three-layer language over two rungs of
 * data that were never shown to anyone (comment 47068 §3). "Why is this link
 * here" and "who does my change affect" had no answer anywhere.
 *
 * `layers` is that answer, and every part of it comes from the HOST:
 *
 *   M3 — this component fetches NOTHING. It imports no base, no `@solum/sdk`,
 *        and knows no entity key; the host hands it the ladder for the surface
 *        being edited, which is also what stops `NavigationPanel` feeding the
 *        console's bar to a PORTAL document (comment 47068 §7, B3).
 *   M2 — `presentRungs` draws the rungs that actually HAVE a row, plus the one
 *        being edited. A fixed three would be wrong on any deployment carrying a
 *        deployment-wide override row, and equally wrong on the ordinary box
 *        that has none.
 *   O3 — the base a pin argues with is `layerBelow(layers)`, computed by the
 *        HOST and passed as `base`. **This HIDES links that pinned users can see
 *        today**: `reconcile` appends every base route the pin does not mention,
 *        so against the platform recipe a link a persona default deliberately
 *        excluded came back the moment somebody reordered their own sidebar.
 *        Against the layer below, it does not. The way back is one click and is
 *        unchanged — Reset drops the pin and the role's list renders as authored.
 *
 * The per-row "from <rung>" mark describes the SAVED ladder, not the unsaved
 * working order in this sheet: it says where a row came from, and dragging it
 * does not retroactively change that until the save round-trips. Order is
 * deliberately never attributed — see `attribution`'s own header.
 *
 * ── what an edit may do, and what it still may not (#1466 B2) ──────────────
 * This paragraph used to read *"reorder and hide, and nothing else"*. Owner
 * ruling M6 (comment 47222) relaxed exactly one of the three invariants that
 * sentence bundled — PARTITION ownership — and the sentence is rewritten rather
 * than left to contradict the code, because the fleet ratchets read source prose
 * and so do the next three people.
 *
 *   MAY   reorder · hide · move a link into any section · create a section and
 *         name it · rename or delete a section YOU created (empty ones only)
 *   MAY NOT  conjure a route · rename a section the DEPLOYMENT declared
 *
 * Still enforced by construction rather than by validation: every row comes out
 * of `rowsFor(base, value)`, which takes existence and item labels from the base
 * bar, and the saved document comes out of `documentFrom(rows)`. So there is
 * still no path from this UI to a route the base bar did not already contain — a
 * person cannot invent a link to a surface they have no business on — and a base
 * section's heading is still the platform's word: a document carrying a renamed
 * base label round-trips back to the base label. A section heading is not a
 * destination; it navigates nowhere and grants nothing (ADR F-39: *"navigation
 * remains a courtesy rather than an authority boundary"*), which is why naming
 * one is a thing a person may do and naming a LINK is not.
 *
 * ── the keyboard is the GUARANTEE; drag is progressive enhancement ─────────
 * This header used to argue the opposite — *"reordering is a pair of BUTTONS,
 * not drag-and-drop"* — and comment 46751 / mailbox 46866 supersede that ruling.
 * The correction is written down rather than left as a silent contradiction,
 * because the fleet ratchets read source prose and so do the next three people.
 *
 * What has NOT changed is which route is load-bearing. The `Move ${label} up` /
 * `Move ${label} down` buttons stay, with those exact accessible names, and they
 * are the route that is guaranteed to work: a bar somebody can only personalise
 * with a mouse is personalisation that excludes exactly the people most likely
 * to need it. Six identical "Move up" buttons in a list are six controls a
 * screen-reader user cannot tell apart, which is why every name says which item
 * it acts on.
 *
 * Drag is ADDED beside them, in two forms that are the same operation:
 *
 *   pointer  — the grip is `draggable`; native HTML5 DnD, no new dependency
 *   keyboard — GRAB MODE on the same grip: Space picks up, the arrows move,
 *              Space drops, Escape cancels and puts it back
 *
 * **Every group move calls `moved(rows, from, to)` and every item move calls
 * `moveItem(rows, from, to)`.** Sections made the second reducer necessary —
 * `moved()` reorders one list and a cross-section drop is two — and it is one
 * reducer, not one per route, for exactly the same reason as before: keyboard
 * parity has to be a fact rather than a claim. It is directly assertable, and
 * `NavigationEditor.test.tsx` asserts it on the SAVED DOCUMENT for both a
 * within-section and a cross-section move. Its mutation record is "make grab
 * mode splice directly instead of calling the reducer" and "stop the arrows at
 * the section boundary".
 *
 * At the boundary of a section the arrows CROSS: down past the last link enters
 * the next section at the top, up past the first enters the previous one at the
 * bottom. Without that, the pointer could reach a document the keyboard could
 * not, which is the same exclusion the Move buttons exist to prevent.
 *
 * A reorder with no announcement is a change a screen-reader user cannot
 * perceive at all — the list simply IS different the next time they read it — so
 * every move, grab, drop, cancel, hide, rename and delete says what happened in
 * a polite live region, and an item move names the DESTINATION section, not the
 * one it left. It is a plain `<p role="status">` and not a new exported
 * primitive on purpose: `inventory.test.ts` pins three exact counts, and any new
 * `packages/ui` export moves all three at once. That is also why `moveItem` and
 * `newSectionId` are imported by path and not re-exported from the barrel.
 *
 * ── Delete refuses OUT LOUD rather than by being unavailable ───────────────
 * A section you created is deletable only when it is empty, and the control is
 * live either way: clicking it on a full one announces how many links to move
 * out first. A `disabled` button is not focusable, so it cannot explain itself
 * to the person most likely to need the explanation — an unreachable control
 * with an unstated rule is how somebody concludes the feature is broken.
 *
 * ── the sheet has a HEIGHT BUDGET, and the host supplies it ────────────────
 * `.scroll` is the only thing that scrolls and `.foot` is sticky, so Save is
 * reachable at 960x480 and on a phone. Measured: the modal panel capped at 68vh
 * with `overflow: hidden`, and this editor renders every group AND every item of
 * a ~37-link bar, so `nav-editor-save` was CLIPPED — not scrolled past,
 * unreachable. In the modal host the panel caps the height; in the page host
 * (`NavigationPanel`) the parent is unconstrained, `max-height: 100%` of an
 * auto-height parent resolves to auto, and the sticky foot pins to the viewport
 * instead. One fix, both hosts, no host edit.
 */
/**
 * ── the ENGINE moved out; this file is now one of its two hosts (ruling 50100)
 *
 * Ruling 50100: *"The current separate Personalize navigation screen is the
 * wrong interaction model."* The console's answer is an in-place editable
 * navbar, which cannot be this markup — the bar is drawn by the console, with
 * the console's router and the console's stylesheet. So everything that is NOT
 * markup left for `navigationEdit.ts` and both surfaces call `useNavigationEdit`.
 *
 * What that leaves here is exactly this sheet's own presentation: the lede, the
 * rung strip, the group/item list and the sticky foot. Not one accessible name,
 * test id or announcement changed in the move — `NavigationEditor.test.tsx` is
 * the proof, and it was not edited for it.
 *
 * This component is still the ADMIN surface (`/personas` → NavigationPanel) and
 * the PORTAL's personalize sheet. It is no longer the console's user-facing
 * personalisation route; that is `apps/console/src/app/NavEditMode.tsx`.
 */
import { useMemo, type ReactNode } from 'react'
import { ChevronDown, ChevronUp, GripVertical, Pencil, Plus, RotateCcw, Trash2 } from 'lucide-react'
import { Button } from '../controls/Button'
import { Input } from '../controls/Input'
import { Switch } from '../controls/Switch'
import { CustomizeRowMenu } from './CustomizeRowMenu'
import {
  type NavDocument, type NavLayer, attribution, presentRungs,
} from './navigationDocument'
import {
  NAV_EDIT_INSTRUCTIONS, useNavigationEdit, type NavEditTarget,
} from './navigationEdit'
import s from './NavigationEditor.module.css'

export interface NavigationEditorProps {
  /**
   * The bar this edit sits on top of — the authority on what exists and on what
   * each link is called.
   *
   * For a USER pin this is `layerBelow(layers)`, not the platform recipe (O3),
   * and the host computes it because the host is the only thing that can read a
   * rung. Absent a ladder it is whatever the host would draw unconfigured, which
   * is the behaviour that shipped.
   */
  base: NavDocument
  /** The document in force for this scope, if any. Supplies order, hidden flags and sections. */
  value?: NavDocument | null
  /**
   * The cascade this bar came out of, platform first, host-supplied (M3).
   *
   * Omitted = no strip and no provenance marks, which is the correct behaviour
   * for a host that cannot read its own rungs: an affordance a host should not
   * offer is a prop it does not pass (`ui-and-experience.md` §1). It is never a
   * fabricated three.
   */
  layers?: NavLayer[]
  /** Whose bar this is. Changes the words, never the mechanics. */
  scope: 'user' | 'persona'
  /** Named in the copy: "your navigation", "the Finance persona". */
  scopeLabel: string
  /** True when `value` is a real stored document rather than the inherited bar. */
  isCustomised: boolean
  busy?: boolean
  /** Host-owned layer/target controls. The shared editor owns their placement,
   * while the app binding owns which authority doors those controls select. */
  layerControl?: ReactNode
  /**
   * Fired whenever a row is picked up or put down in grab mode.
   *
   * A host that renders this inside a dismissable overlay MUST use it. Radix's
   * dismissable layer registers its Escape handler on `ownerDocument` with
   * `{ capture: true }`, so it always runs before this component's own bubble
   * handler: in the console's only shipping host, Escape during a grab closed
   * the whole sheet and discarded every unsaved reorder, while the grip's
   * sr-only instructions promised it would put the row back. The host answers
   * by passing `dismissable={false}` while a row is held (#1466 A review).
   */
  onGrabChange?: (held: boolean) => void
  onSave: (doc: NavDocument) => void
  /** `pin_navigation(null)` / `clear_navigation(...)`. Absent = nothing to undo. */
  onReset?: () => void
  /** Defaults to navigation wording; other shared list grammars name their inherited rung. */
  resetLabel?: string
  onCancel?: () => void
}

export function NavigationEditor({
  base, value, layers, scope, scopeLabel, isCustomised, busy, layerControl,
  onGrabChange, onSave, onReset, resetLabel, onCancel,
}: NavigationEditorProps) {
  const nav = useNavigationEdit({ base, value, busy, onGrabChange })
  const { rows } = nav
  /* The ladder, resolved once per ladder rather than per row: `attribution`
     walks every rung's every item, and the item list is ~37 long in the console
     host. Both are pure functions of `layers` and neither reads `rows`, which is
     the point — they describe what was SAVED, not what is being dragged. */
  const rungs = useMemo(() => presentRungs(layers), [layers])
  const marks = useMemo(() => attribution(layers), [layers])
  /* The rung everything starts on. A row attributed HERE is the bar as the
     deployment shipped it, and marking all thirty-seven of those would be noise
     that hides the two rows somebody actually decided about. */
  const originRung = rungs[0]?.key
  const rungLabel = useMemo(
    () => new Map((layers ?? []).map((l) => [l.key, l.label])), [layers])
  const editedRung = rungs.find((r) => r.editing)
  const markFor = (key: string | undefined) =>
    (key && key !== originRung ? rungLabel.get(key) : undefined)

  const grip = (t: NavEditTarget, label: string) => (
    <Button size="sm" variant="ghost" icon={<GripVertical />}
            className={s.handle} {...nav.gripProps(t, label)} />
  )

  return (
    <div className={s.editor} data-testid="navigation-editor">
      <div className={s.top}>
        <p className={s.lede}>
          Reorder, hide and group what you see in {scopeLabel}. {scope === 'persona'
            ? 'This is the default everyone in that role gets before they personalise it themselves.'
            : 'Only you see this.'}
        </p>

        {isCustomised && (
          <p className={s.state} data-testid="nav-editor-state">
            <em className={s.custom}>· customised</em>
          </p>
        )}

        {layerControl && <div className={s.layer}>{layerControl}</div>}

        {/* An ordered list, because the rungs ARE ordered and a screen reader
            should say "2 of 3" rather than reading four unrelated words. The
            edited rung carries `aria-current="step"`; nothing here is a control,
            so nothing here is focusable. */}
        {rungs.length > 1 && (
          <>
            <ol className={s.rungs} data-testid="nav-editor-rungs">
              {rungs.map((r) => (
                <li key={r.key} className={s.rung} data-rung={r.key}
                    data-editing={r.editing || undefined}
                    aria-current={r.editing ? 'step' : undefined}>{r.label}</li>
              ))}
            </ol>
            {editedRung && (
              <p className={s.lede} data-testid="nav-editor-lands">
                Built up in {rungs.length} steps. Your changes are saved to{' '}
                <strong>{editedRung.label}</strong> and change nothing below it.
              </p>
            )}
          </>
        )}

        {/* Named by every grip, so the gesture is discoverable without being
            repeated thirty-seven times into the accessibility tree. */}
        <p id={nav.instructionsId} className={s.srOnly}>{NAV_EDIT_INSTRUCTIONS}</p>

        <div className={s.actions}>
          <Button size="sm" variant="ghost" icon={<Plus />} disabled={busy}
                  data-testid="nav-add-section" onClick={nav.addSection}>Add section</Button>
        </div>
      </div>

      <div className={s.scroll}>
        <ul className={s.groups}>
          {rows.map((g, gi) => {
            const t: NavEditTarget = { kind: 'group', index: gi }
            const groupKey = g.id ?? g.label
            const occurrence = rows.slice(0, gi).filter((row) => (row.id ?? row.label) === groupKey).length
            // Two sections may share a NAME; only the id keeps them two, and a
            // rename must not remount the row out from under a live grab.
            return (
              <li key={occurrence ? `${groupKey}-${occurrence}` : groupKey}
                  className={s.group} data-hidden={g.hidden || undefined}
                  data-recovery={g.recovery || undefined}
                  data-grabbed={nav.isGrabbed(t) || undefined}
                  data-dragging={nav.isDragging(t) || undefined}
                  {...nav.dropProps(t)}>
                <div className={s.head}>
                  {!g.recovery && grip(t, g.label)}
                  {nav.renaming === g.id ? (
                    <Input className={s.rename} {...nav.renameProps(gi)} />
                  ) : (
                    <span className={s.name} id={g.recovery ? 'nav-recovery-section' : undefined}
                      tabIndex={g.recovery ? -1 : undefined}>{g.label}</span>
                  )}
                  {markFor(marks.groups.get(g.id ?? g.label)) && (
                    <span className={s.from} data-testid="nav-group-rung">
                      from {markFor(marks.groups.get(g.id ?? g.label))}
                    </span>
                  )}
                  <span className={s.sp} />
                  {g.id && !g.recovery && nav.renaming !== g.id && (
                    <Button size="sm" variant="ghost" icon={<Pencil />} disabled={busy}
                            aria-label={`Rename ${g.label}`}
                            onClick={() => nav.beginRename(gi)} />
                  )}
                  {g.id && !g.recovery && (
                    <Button size="sm" variant="ghost" icon={<Trash2 />} disabled={busy}
                            aria-label={`Delete ${g.label}`}
                            onClick={() => nav.deleteSection(gi)} />
                  )}
                  {!g.recovery && <Button size="sm" variant="ghost" icon={<ChevronUp />} disabled={gi === 0 || busy}
                          aria-label={`Move ${g.label} up`}
                          onClick={() => nav.move(t, { kind: 'group', index: gi - 1 })} />}
                  {!g.recovery && <Button size="sm" variant="ghost" icon={<ChevronDown />}
                          disabled={gi === rows.length - 1 || rows[gi + 1]?.recovery || busy}
                          aria-label={`Move ${g.label} down`}
                          onClick={() => nav.move(t, { kind: 'group', index: gi + 1 })} />}
                  {!g.recovery && <Switch checked={!g.hidden} disabled={busy}
                          aria-label={`Show the ${g.label} group`}
                          onCheckedChange={(on) => nav.setGroupHidden(gi, !on)} />}
                </div>
                <ul className={s.items} {...nav.listDropProps(gi)}>
                  {g.items.map((it, ii) => {
                    const ti: NavEditTarget = { kind: 'item', gi, index: ii }
                    return (
                      <li key={it.to} className={s.item} data-hidden={it.hidden || undefined}
                          data-grabbed={nav.isGrabbed(ti) || undefined}
                          data-dragging={nav.isDragging(ti) || undefined}
                          {...nav.dropProps(ti)}>
                        {grip(ti, it.label)}
                        <span className={s.itemName}>{it.label}</span>
                        {markFor(marks.items.get(it.to)) && (
                          <span className={s.from} data-testid="nav-item-rung">
                            from {markFor(marks.items.get(it.to))}
                          </span>
                        )}
                        <span className={s.sp} />
                        <Button size="sm" variant="ghost" icon={<ChevronUp />} disabled={ii === 0 || busy}
                                aria-label={`Move ${it.label} up`}
                                onClick={() => nav.move(ti, { kind: 'item', gi, index: ii - 1 })} />
                        <Button size="sm" variant="ghost" icon={<ChevronDown />} disabled={ii === g.items.length - 1 || busy}
                                aria-label={`Move ${it.label} down`}
                                onClick={() => nav.move(ti, { kind: 'item', gi, index: ii + 1 })} />
                        <Switch checked={!it.hidden} disabled={busy}
                                aria-label={`Show ${it.label}`}
                                onCheckedChange={(on) => nav.setItemHidden(gi, ii, !on)} />
                        {g.recovery && <CustomizeRowMenu label={it.label}
                          sections={rows.flatMap((section, sectionIndex) => section.recovery ? [] : [{
                            key: section.id ?? section.label,
                            label: section.label,
                            onSelect: () => nav.move(ti, {
                              kind: 'item', gi: sectionIndex, index: section.items.length,
                            }),
                          }])} />}
                      </li>
                    )
                  })}
                  {/* An empty section is a drop target with nothing in it, which
                      reads as a bug rather than an invitation. */}
                  {g.items.length === 0 && (
                    <li className={s.empty} data-testid="nav-section-empty">
                      Drag links here, or use Space and the arrows.
                    </li>
                  )}
                </ul>
              </li>
            )
          })}
        </ul>
      </div>

      <p role="status" aria-live="polite" className={s.srOnly}
         data-testid="nav-editor-announce">{nav.said}</p>

      {/* Sticky, and it holds ALL the actions — Reset moved out of the state
          line so the third one is not stranded above a scroll region. The count
          sits here too, because "the bar will be empty" is precisely the
          sentence somebody needs at the moment they reach for Save. */}
      <div className={s.foot}>
        {/* Said out loud, because hiding everything is a reachable state and a
            blank bar with no explanation is how somebody concludes the app is
            broken. The save is still allowed — it is their bar. */}
        <span className={s.count} data-testid="nav-editor-count">
          {nav.visibleCount === 0 ? 'Nothing is showing — the bar will be empty.' : `${nav.visibleCount} links showing`}
        </span>
        {nav.recoveryCount > 0 && (
          <Button size="sm" variant="ghost" data-testid="nav-recovery-warning"
            onClick={() => document.getElementById('nav-recovery-section')?.focus()}>
            {nav.recoveryCount} {nav.recoveryCount === 1 ? 'section needs' : 'sections need'} a group
          </Button>
        )}
        {nav.excludedCount > 0 && <span className={s.count}>
          {nav.excludedCount} unavailable {nav.excludedCount === 1 ? 'link was' : 'links were'} left out
        </span>}
        <span className={s.sp} />
        {isCustomised && onReset && (
          <Button size="sm" variant="ghost" data-testid="nav-editor-reset"
                  icon={<RotateCcw />} disabled={busy} onClick={onReset}>
            {resetLabel ?? `Reset ${scope === 'persona' ? 'to the built-in navigation' : 'to my default'}`}
          </Button>
        )}
        {onCancel && <Button size="sm" variant="ghost" disabled={busy} onClick={onCancel}>Cancel</Button>}
        <Button size="sm" disabled={busy || !nav.canSave} data-testid="nav-editor-save"
                onClick={() => onSave(nav.document())}>Save</Button>
      </div>
    </div>
  )
}
